/* ═══════════════════════════════════════════════════════════════
   zone-measure — the preview's measurer for shared/zone-layout.ts.

   The Formas preview and the Word export run the SAME layout brain
   (shared/zone-layout.ts); only the measurer differs. To keep their
   decisions identical, the preview measures with the SAME baked
   advance-width tables the engine falls back to (extracted from the
   real fonts). Fonts outside the baked set use canvas measureText —
   the browser renders with the same installed font files, so the
   numbers still track Word closely.
   ═══════════════════════════════════════════════════════════════ */

import { BAKED_FONT_METRICS, type BakedFace } from '../../shared/font-metrics-data';
import {
  parseTokenMods,
  modSizePt,
  modFont,
  tokenMeasureText,
  type TokenTextCtx,
  type ZoneMeasure,
} from '../../shared/zone-layout';

function bakedFace(family: string, bold: boolean): BakedFace | null {
  const fam = BAKED_FONT_METRICS[(family || '').toLowerCase()];
  if (!fam) return null;
  return (bold ? fam.bold || fam.regular : fam.regular) || null;
}

let canvasCtx: CanvasRenderingContext2D | null | undefined;
function getCanvas(): CanvasRenderingContext2D | null {
  if (canvasCtx !== undefined) return canvasCtx;
  try {
    canvasCtx = document.createElement('canvas').getContext('2d');
  } catch {
    canvasCtx = null;
  }
  return canvasCtx;
}

/** Width of `text` in twips — baked tables first, canvas fallback. */
export function measureTextTwipsPreview(
  text: string,
  sizePt: number,
  family: string,
  bold = false,
  italic = false
): number {
  if (!text) return 0;
  const baked = bakedFace(family, bold);
  if (baked) {
    let units = 0;
    for (const ch of text) {
      const code = ch.codePointAt(0)!;
      units += baked.adv[code] ?? baked.avg;
    }
    let pt = (units / baked.upem) * sizePt;
    if (bold && !baked.isBold) pt *= 1.03;
    return Math.ceil(pt * 20);
  }
  const ctx = getCanvas();
  if (ctx) {
    ctx.font = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${sizePt}pt "${family}"`;
    const px = ctx.measureText(text).width;
    // px → pt (×72/96) → twips, small cushion for renderer differences
    return Math.ceil(px * 0.75 * 1.05 * 20);
  }
  // no canvas (tests/SSR): crude but safe over-estimate
  return Math.ceil(text.length * 0.6 * sizePt * 1.15 * 20);
}

export interface ZoneMeasureOpts {
  sizePt: number;
  family: string;
  ctx: TokenTextCtx;
  /** forma images (for {Image:id} width), px at 96dpi */
  images?: Record<string, { width: number; height: number }>;
}

/** Build the ZoneMeasure callback for layoutZones — mirrors the engine's
 *  measureLineTwips exactly (inter-token spaces, mods, image scaling, ×1.02). */
export function makeZoneMeasure(opts: ZoneMeasureOpts): ZoneMeasure {
  const { sizePt, family, ctx, images } = opts;
  return (lineTokens: string[]): number => {
    let tw = 0;
    lineTokens.forEach((token, idx) => {
      if (token === '\n') return;
      const { base, mods } = parseTokenMods(token);
      const prevBase = idx > 0 ? parseTokenMods(lineTokens[idx - 1]).base : null;
      if (idx > 0 && lineTokens[idx - 1] !== '\n' && base !== '{Space}' && prevBase !== '{Space}') {
        tw += measureTextTwipsPreview(' ', sizePt, family);
      }
      if (base.startsWith('{Image:')) {
        const close = base.indexOf('}');
        const inner = base.slice(7, close < 0 ? undefined : close);
        const pipe = inner.indexOf('|');
        const id = pipe < 0 ? inner : inner.slice(0, pipe);
        const img = images?.[id];
        if (img) {
          const maxH = 457200; // 0.5in EMU — mirrors engine emission scaling
          let cx = img.width * 9525;
          const cy = img.height * 9525;
          if (cy > maxH) cx = Math.round(cx * (maxH / cy));
          tw += Math.ceil(cx / 635);
        }
        return;
      }
      const bold = mods.has('bold');
      const italic = mods.has('italic');
      // A token's own size:N mod overrides the zone's base size for its own
      // glyphs only — inter-token spaces stay at the base size (the run the
      // engine emits for them is never token-scoped either; see word-engine's
      // processTokenRuns, which always uses the shared rPrXml for spaces).
      const effSize = modSizePt(mods) ?? sizePt;
      const text = tokenMeasureText(token, ctx);
      const effFamily = modFont(mods) ?? family;
      const widthOf = (s: string) => measureTextTwipsPreview(s, effSize, effFamily, bold, italic);
      const widest = text.split('\n').reduce((a, b) => (widthOf(b) > widthOf(a) ? b : a), '');
      tw += widthOf(widest);
    });
    return Math.ceil(tw * 1.02);
  };
}
